import {yupResolver} from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Container, Checkbox, List, Button, Label, Grid, Form, Popup, Icon} from 'semantic-ui-react';
import * as yup from 'yup';

import {wordpressSubdomain, validationSchemas} from 'components/Common/sharedElements';
import {getChangedFieldsValues} from 'forms';
import {useReservedHostnames} from 'hooks';

import QrCodeModal from '../../../QrCode/QrCodeModal';
import RemoveWordPressEnvironmentModal from '../RemoveWordPressEnvironmentModal';

import styles from './Aliases.module.scss';

const formSchema = yup.object().shape({
  name: validationSchemas.wordpressSiteURLValidation.test(
    'domainIsUnique',
    'Such domain already exists',
    (value, ctx) => {
      const siteDomain = value + wordpressSubdomain;

      if (ctx.options.context.reservedHostnames.includes(value)) {
        return ctx.createError({message: 'Provided site URL is a reserved name'});
      }

      return !ctx.options.context.reservedFullyQualifiedDomainNames.includes(siteDomain);
    }
  ),
});

const defaultFormValues = {
  name: '',
};

export default function Aliases({aliases, onAliasCreate, onAliasRemove}) {
  const {
    reservedHostnames,
    reservedFullyQualifiedDomainNames,
    isLoading: isLoadingReservedHostnames,
  } = useReservedHostnames('wordpress');

  const [showInput, setShowInput] = useState(false);
  const [removeAliases, setRemoveAliases] = useState([]);
  // QR code
  const [qrUrl, setQrUrl] = useState('');
  const {
    control,
    handleSubmit,
    formState: {dirtyFields, isDirty, isValid, isSubmitting, isSubmitSuccessful},
    reset,
  } = useForm({
    context: {reservedHostnames, reservedFullyQualifiedDomainNames},
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(formSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
      setShowInput(false);
    }
  }, [isSubmitSuccessful, reset]);

  const submitHandler = async data => {
    const values = getChangedFieldsValues(dirtyFields, data);

    if (onAliasCreate && values.name) {
      await onAliasCreate(values.name + wordpressSubdomain);
    }
  };

  const qrCode = url => {
    return (
      <Popup
        trigger={
          <Icon
            className={styles.QrCodeIcon}
            name="qrcode"
            onClick={() => {
              const currentUrl = `https://${url}`;
              setQrUrl(currentUrl);
            }}
          />
        }
        position="top center"
      >
        Generate QR code
      </Popup>
    );
  };

  return (
    <Container className={styles.Aliases}>
      {!showInput ? (
        <Button
          key="newAlias"
          labelPosition="left"
          className={styles.ActionButton}
          icon="add"
          content="Add Alias"
          onClick={() => setShowInput(true)}
        />
      ) : (
        <Form onSubmit={handleSubmit(submitHandler)} loading={isSubmitting}>
          <Grid stackable>
            <Grid.Row columns={2}>
              <Grid.Column textAlign="left" verticalAlign="top" width={10}>
                <Controller
                  control={control}
                  name="name"
                  render={({field: {onChange, value, name}, fieldState: {error}}) => (
                    <Form.Input
                      id="alias"
                      onChange={(_evt, data) => {
                        onChange(data.value);
                      }}
                      labelPosition="right"
                      name={name}
                      value={value}
                      error={error?.message}
                      fluid
                      required
                    >
                      <input />
                      <Label>{wordpressSubdomain}</Label>
                    </Form.Input>
                  )}
                />
              </Grid.Column>
              <Grid.Column width={6}>
                <Button.Group className={styles.ButtonGroup}>
                  <Button
                    type="submit"
                    className={styles.AddButton}
                    disabled={!isValid || !isDirty || isSubmitting || isLoadingReservedHostnames}
                  >
                    Add
                  </Button>
                  <Button
                    onClick={() => {
                      reset();
                      setShowInput(false);
                    }}
                  >
                    Cancel
                  </Button>
                </Button.Group>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      )}
      <List>
        {aliases.map(alias => (
          <List.Item className={styles.ListItem} key={alias}>
            <Checkbox
              key={alias}
              id={alias}
              onChange={(_evt, data) => {
                if (data.checked) {
                  setRemoveAliases([...removeAliases, alias]);
                } else {
                  setRemoveAliases(removeAliases.filter(siteAlias => siteAlias !== alias));
                }
              }}
              label={
                <label>
                  <a
                    className={styles.Link}
                    href={`https://${alias}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {alias}
                  </a>
                  {qrCode(alias)}
                </label>
              }
            ></Checkbox>
          </List.Item>
        ))}
      </List>
      {aliases.length > 0 && (
        <RemoveWordPressEnvironmentModal
          trigger={
            <Button
              type="button"
              className={styles.DeleteButton}
              disabled={removeAliases.length === 0}
            >
              Delete
            </Button>
          }
          successMessage="Selected aliases were deleted successfully"
          content={
            <div className="content">
              You are about to delete the selected aliases. Are you sure that you want to proceed?
              Please note that it may take some time for the URL(s) to be available for reuse again.
            </div>
          }
          confirmButtonContent="Delete"
          onConfirm={async () => await onAliasRemove(removeAliases)}
        />
      )}
      {qrUrl && (
        <QrCodeModal
          siteUrl={qrUrl}
          onClose={() => {
            setQrUrl('');
          }}
        />
      )}
    </Container>
  );
}

Aliases.propTypes = {
  aliases: PropTypes.array.isRequired,
  onAliasCreate: PropTypes.func,
  onAliasRemove: PropTypes.func,
};
